/**
 * Post-OTP address onboarding (one-shot handoff).
 * sessionStorage alone is unreliable after process death on some WebViews;
 * a consumed localStorage token + ?fromAuth= makes cold restore unlock the screen.
 *
 * Rule: a signed-in user without a saved address is always sent to /my-location.
 * "/" renders <Redirect to="/splashscreen" /> when there is no address, and
 * /splashscreen is the login screen, so sending them home reload-loops the app.
 */

import { flushThenNavigate } from "./persistFlush";

export const FORCE_ADDRESS_SETUP_KEY = "forceAddressSetupAfterAuth";
export const ADDRESS_SETUP_FLOW_KEY = "addressSetupFlow";
export const ADDRESS_SETUP_TOKEN_KEY = "addressSetupToken";

export const ADDRESS_SETUP_PATH = "/my-location";

export const markForceAddressSetup = () => {
  try {
    sessionStorage.setItem(FORCE_ADDRESS_SETUP_KEY, "1");
    sessionStorage.setItem(ADDRESS_SETUP_FLOW_KEY, "1");
  } catch (e) {
    // ignore
  }
};

export const clearForceAddressSetup = () => {
  try {
    sessionStorage.removeItem(FORCE_ADDRESS_SETUP_KEY);
    sessionStorage.removeItem(ADDRESS_SETUP_FLOW_KEY);
    localStorage.removeItem(ADDRESS_SETUP_TOKEN_KEY);
  } catch (e) {
    // ignore
  }
};

export const isForceAddressSetupActive = () => {
  try {
    return sessionStorage.getItem(FORCE_ADDRESS_SETUP_KEY) === "1";
  } catch (e) {
    return false;
  }
};

export const hasValidUserSetAddress = () => {
  try {
    const raw = localStorage.getItem("userSetAddress");
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!(parsed && parsed.lat && parsed.lng);
  } catch (e) {
    return false;
  }
};

/** Full reload navigation that no-ops on the current URL, so it can never loop. */
export const hardNavigate = (path) => {
  if (typeof window === "undefined") return;
  const current = `${window.location.pathname}${window.location.search}`;
  if (current === path) return;
  flushThenNavigate(path);
};

/** Save Address destination: locked (?setup=1) only while the post-OTP flow is live. */
export const addressSetupPath = () =>
  isForceAddressSetupActive() ? `${ADDRESS_SETUP_PATH}?setup=1` : ADDRESS_SETUP_PATH;

/** Starts the locked post-OTP flow and returns the URL carrying its one-shot token. */
export const beginAddressSetup = () => {
  const token = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    localStorage.setItem(ADDRESS_SETUP_TOKEN_KEY, token);
  } catch (e) {
    // ignore
  }
  markForceAddressSetup();
  return `${ADDRESS_SETUP_PATH}?setup=1&fromAuth=${encodeURIComponent(token)}`;
};

/** Navigate after auth: home if address exists, else the one-time Save Address setup. */
export const redirectAfterAuth = (hasValidAddress) => {
  if (hasValidAddress) {
    clearForceAddressSetup();
    hardNavigate("/");
    return;
  }
  hardNavigate(beginAddressSetup());
};

/**
 * Validate the OTP handoff on Save Address mount.
 * @returns {"enter_setup"|"unlock_setup"|"stay"}
 */
export const resolveAddressSetupHandoff = (search) => {
  let params;
  try {
    params = new URLSearchParams(search || "");
  } catch (e) {
    params = new URLSearchParams();
  }

  const setup = params.get("setup") === "1";
  if (!setup) {
    return isForceAddressSetupActive() ? "stay" : "unlock_setup";
  }

  const fromAuth = params.get("fromAuth");
  let token = null;
  try {
    token = localStorage.getItem(ADDRESS_SETUP_TOKEN_KEY);
  } catch (e) {
    token = null;
  }

  if (fromAuth && token && fromAuth === token) {
    try {
      localStorage.removeItem(ADDRESS_SETUP_TOKEN_KEY);
    } catch (e) {
      // ignore
    }
    markForceAddressSetup();
    return "enter_setup";
  }

  // Restored ?setup=1 after an app kill / stale link: show the page, but unlocked.
  if (!isForceAddressSetupActive()) {
    clearForceAddressSetup();
    return "unlock_setup";
  }

  return "stay";
};
